import { PricesBase } from '../types';
import calculateTotal from './computePrice/calculateTotal';

type GetPricesWithTotalProps = PricesBase & {
  discountPrices?: PricesBase;
};

type GetPricesWithTotalReturn<T> = T & {
  total: number;
  discountPrices?: PricesBase & {
    total: number;
  };
};

/**
 * Calculates total prices by combining base prices with per-employee costs
 *
 * @param pricesRange - Price range object containing base prices and optional discount prices
 * @param nbEmployee - Number of employees for calculating total prices
 *
 * @returns An enhanced price object that includes:
 * - All original price fields
 * - Calculated total monthly price
 * - Optional discount prices with their own total monthly price
 *
 * The function:
 * 1. Ensures all base price fields have at least 0 as value
 * 2. Calculates total monthly price for base prices
 * 3. If discount prices exist:
 *    - Ensures all discount price fields have at least 0 as value
 *    - Calculates total monthly price for discount prices
 *
 * @example
 * ```ts
 * const prices = {
 *   base: 100,
 *   price: 50,
 *   obFees: 500,
 *   discountPrices: {
 *     base: 80,
 *     price: 40,
 *     obFees: 400
 *   }
 * };
 *
 * getPricesWithTotal(prices, 5);
 * // Returns: {
 * //   base: 100,
 * //   price: 50,
 * //   obFees: 500,
 * //   total: 350, // 100 + (50 * 5)
 * //   discountPrices: {
 * //     base: 80,
 * //     price: 40,
 * //     obFees: 400,
 * //     total: 280 // 80 + (40 * 5)
 * //   }
 * // }
 * ```
 */
export const getPricesWithTotal = <T extends GetPricesWithTotalProps>(
  pricesRange: T,
  nbEmployee: number,
): GetPricesWithTotalReturn<T> => {
  const basePrice = {
    ...pricesRange,
    base: pricesRange.base ?? 0,
    price: pricesRange.price ?? 0,
    obFees: pricesRange.obFees ?? 0,
    total: calculateTotal({
      base: pricesRange.base ?? 0,
      price: pricesRange.price ?? 0,
      nbEmployee,
    }),
  };

  const discountPrices = pricesRange.discountPrices
    ? {
        base: pricesRange.discountPrices.base ?? pricesRange.base ?? 0,
        price: pricesRange.discountPrices.price ?? pricesRange.price ?? 0,
        obFees: pricesRange.discountPrices.obFees ?? pricesRange.obFees ?? 0,
        total: calculateTotal({
          base: pricesRange.discountPrices.base ?? pricesRange.base ?? 0,
          price: pricesRange.discountPrices.price ?? pricesRange.price ?? 0,
          nbEmployee,
        }),
      }
    : undefined;
  return {
    ...basePrice,
    discountPrices,
  };
};
